import { defineStore } from "pinia";
import raw from "../mocked/portfolios.json";
import {
  buildCurves,
  tickCurves,
  priceNdfRows,
  ndfTotals,
  KNOWN_PAIRS,
} from "@/utils/ndfPortfolio";

export const useDisplayStore = defineStore("display", {
  state: () => ({
    breakpoint: undefined,
    windowHeight: window.innerHeight,
    headerHeight: 153,
    marginLayout: 32,
  }),
  getters: {
    isMobile() {
      return this.mobile
    },
    currentBreakpoint() {
      return this.breakpoint
    },
    isXLarge() {
      return this.currentBreakpoint === "xl"
    },
    isLarge() {
      return this.currentBreakpoint === "lg"
    },
    isMedium() {
      return this.currentBreakpoint === "md"
    },
    isSmall() {
      return this.currentBreakpoint === "sm"
    },
    isXSmall() {
      return this.currentBreakpoint === "xs"
    },
    contentHeight() {
      const contentHeight = this.windowHeight - this.headerHeight;
      return `${contentHeight}`
    },
  },
  actions: {},
});

export const useColorsStore = defineStore("utils", {
  state: () => ({
    warningColor: "#ff8a3d", // "#FFD600", // "#FB8C00",
    successColor: "#26c281",
    dangerColor: "#e55353",
    yellowTransp: "#4b2000", // "#816c00" // "#633b01"
    aliveTransp: "#003817",
  }),
  actions: {},
});

// --- Normalização do JSON: aceita array na raiz OU { portfolios: [...] }
const seed = Array.isArray(raw) ? raw : raw?.portfolios || [];

// Utilitário para normalizar linhas do CSV para as chaves do "CSV"
function normalizeCsvRows(rows = []) {
  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  return rows.map((r) => {
    const symbol = (r.Symbol ?? r.symbol ?? "").toString().trim();
    const name = (r.Name ?? r.name ?? "").toString().trim();
    const quantity = toNum(r.Quantity ?? r.quantity);
    const avgCost = toNum(r.AvgCost ?? r.avgCost);
    const currency = (r.Currency ?? r.currency ?? "USD").toString().trim();
    const aggregationLabel = (
      r.AggregationLabel ??
      r["Aggregation Label"] ??
      r.aggregationLabel ??
      r.aggregation ??
      ""
    )
      .toString()
      .trim();
    return { symbol, name, quantity, avgCost, currency, aggregationLabel };
  });
}

// Linhas de NDF nao tem quantidade nem custo medio: tem par, ponta, notional,
// taxa contratada e vencimento. Ver [[ndfPortfolio]] para o porque.
function normalizeNdfRows(rows = []) {
  const toNum = (v) => {
    const n = Number(String(v ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
  };
  const pick = (r, ...keys) => {
    for (const k of keys) if (r[k] != null && r[k] !== "") return r[k];
    return "";
  };
  return rows.map((r) => {
    const pair = String(pick(r, "Pair", "pair", "Par", "par", "Symbol", "symbol"))
      .trim()
      .toUpperCase()
      .replace("/", "");
    const rawSide = String(pick(r, "Side", "side", "Ponta", "ponta")).trim().toUpperCase();
    const side = ["SELL", "VENDA", "V", "S"].includes(rawSide) ? "SELL" : "BUY";
    return {
      pair: pair || "USDBRL",
      side,
      notional: toNum(pick(r, "Notional", "notional", "Nocional", "nocional")),
      strike: toNum(pick(r, "Strike", "strike", "Taxa", "taxa", "Rate", "rate")),
      tradeDate: String(pick(r, "TradeDate", "tradeDate", "DataOperacao", "dataOperacao")).trim(),
      maturity: String(pick(r, "Maturity", "maturity", "Vencimento", "vencimento")).trim(),
      counterparty: String(pick(r, "Counterparty", "counterparty", "Contraparte", "contraparte")).trim(),
      fixingSource: String(pick(r, "FixingSource", "fixingSource", "Fixing", "fixing")).trim() || "PTAX D-1",
      currency: pair.slice(0, 3) || "USD",
    };
  });
}

// Parser CSV simples
function parseCsv(text) {
  const lines = (text || "").trim().split(/\r?\n/);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const cols = line.split(",").map((c) => c.trim());
    const obj = {};
    headers.forEach((h, i) => {
      obj[h] = cols[i];
    });
    return obj;
  });
}

// Portfolio sem produto declarado e ETF: o produto nasceu depois dos dados.
const productOf = (p) => (p && p.product ? p.product : "ETF");

const firstOfProduct = (portfolios, product) =>
  portfolios.find((p) => productOf(p) === product) || null;

export const usePortfolioStore = defineStore("portfolio", {
  state: () => ({
    portfolios: seed,
    positions: seed[0]?.positions ? [...seed[0].positions] : [],
    selectedPortfolioId: seed[0]?.id || null,
    portfolioState: true,
    // 'ETF' | 'NDF' — escolhido no seletor da sidebar do Port.AR.
    productType: "ETF",
    // Curvas forward por PAR (nao por simbolo): dois contratos do mesmo par
    // com vencimentos diferentes leem pontos diferentes da mesma curva.
    ndfCurves: buildCurves(KNOWN_PAIRS),
    // Fotografia da abertura, congelada: a "variacao do dia" do MTM e medida
    // contra ela, nao contra o tick anterior.
    ndfOpenCurves: null,
  }),

  getters: {
    // Filtrado pelo produto ativo — a lista da sidebar so mostra portfolios do
    // produto selecionado, senao a tela de NDF renderiza linhas de ETF.
    getPortfolios: (state) =>
      state.portfolios.filter((p) => productOf(p) === state.productType),
    getAllPortfolios: (state) => state.portfolios,
    getPositions: (state) => state.positions,
    isNdf: (state) => state.productType === "NDF",
    selectedPortfolio(state) {
      return (
        state.portfolios.find((p) => p.id === state.selectedPortfolioId) || null
      );
    },
    getStatus: (state) => state.portfolioState,

    // Linhas de NDF ja precificadas contra a curva corrente. Vive no store (e
    // nao no componente da tabela) porque o painel de propriedades da direita
    // precisa dos mesmos numeros.
    ndfRows(state) {
      if (productOf(this.selectedPortfolio) !== "NDF") return [];
      return priceNdfRows(
        state.positions,
        state.ndfCurves,
        new Date(),
        state.ndfOpenCurves
      );
    },
    ndfSummary() {
      return ndfTotals(this.ndfRows);
    },
  },
  actions: {
    togglePortfolioStatus() {
      this.portfolioState = !this.portfolioState;
    },
    setProductType(product) {
      if (product !== "ETF" && product !== "NDF") return;
      this.productType = product;
      // Trocar de produto sem trocar de portfolio deixaria a tela de NDF
      // lendo posicoes de ETF (ou o contrario) — colunas vazias, sem erro.
      if (productOf(this.selectedPortfolio) !== product) {
        const next = firstOfProduct(this.portfolios, product);
        if (next) this.selectPortfolio(next.id);
        else {
          this.selectedPortfolioId = null;
          this.positions = [];
        }
      }
      if (product === "NDF" && !this.ndfOpenCurves) this.snapshotNdfOpen();
    },
    // Idempotente de proposito: navegar entre as abas remonta o componente, e
    // re-fotografar a abertura zeraria a variacao do dia sem motivo.
    snapshotNdfOpen(force = false) {
      if (this.ndfOpenCurves && !force) return;
      this.ndfOpenCurves = JSON.parse(JSON.stringify(this.ndfCurves));
    },
    tickNdfCurves() {
      if (!this.ndfOpenCurves) this.snapshotNdfOpen();
      this.ndfCurves = tickCurves(this.ndfCurves);
    },
    selectPortfolio(id) {
      this.selectedPortfolioId = id;
      const p = this.portfolios.find((x) => x.id === id);
      // Trocar a referência garante reatividade no Vue
      this.positions = Array.isArray(p?.positions) ? [...p.positions] : [];
    },
    deletePortfolioWithPosition(id) {
      this.portfolios = this.portfolios.filter((p) => p.id !== id);
      if (this.selectedPortfolioId === id) {
        // Cai no proximo portfolio DO MESMO produto, nao no primeiro da lista.
        const next = firstOfProduct(this.portfolios, this.productType);
        this.selectedPortfolioId = next?.id || null;
        this.positions = next?.positions ? [...next.positions] : [];
      }
    },
    addPortfolioWithPositions({ name, currency = "USD", rows = [], product = null }) {
      // Suporta dois formatos:
      // A) rows = [{ positions: [...] }, ...]
      // B) rows = [{ Symbol, Name, Quantity, AvgCost, ... }, ...] (CSV parseado)
      const base = Array.isArray(rows)
        ? rows.flatMap((r) => r?.positions || r) // se não existir "positions", usa o próprio objeto da linha
        : [];
      // Um import nasce no produto que esta selecionado — e as colunas do CSV
      // sao outras em cada um.
      const productType = product || this.productType;
      const positions =
        productType === "NDF" ? normalizeNdfRows(base) : normalizeCsvRows(base);

      const id = `csv_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const date =
        rows[0] && rows[0].date ? rows[0].date : new Date().toISOString();
      const quantity = positions.length;
      const portfolio = { id, date, name, product: productType, currency, quantity, positions };

      this.portfolios = [portfolio, ...this.portfolios];
      this.selectPortfolio(id);
      return portfolio;
    },
    importCsvText(text, nameHint = "Imported Portfolio", currencyHint = "USD") {
      const rawRows = parseCsv(text);
      if (!rawRows.length) return null;
      return this.addPortfolioWithPositions({
        name: nameHint,
        currency: currencyHint,
        rows: rawRows,
      });
    },
    importMultipleCsv(files) {
      for (const f of files) {
        this.importCsvText(f.text, f.name, f.currency || "USD");
      }
    },
    unrealizedPnL() {
      const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
      const totalSum = this.positions.reduce((sum, p) => {
        const qty = num(p.quantity);
        const avg = num(p.avgCost);
        const last = p.last != null ? num(p.last) : 95; //mocked price

        return sum + (last - avg) * qty;
      }, 0);

      if (totalSum >= 1e9 || totalSum <= -1e9) {
        return (totalSum / 1e9).toFixed(2) + "B";
      }

      if (totalSum >= 1e6 || totalSum <= -1e6) {
        return (totalSum / 1e6).toFixed(2) + "M";
      }

      if (totalSum >= 1e3 || totalSum <= -1e3) {
        return (totalSum / 1e3).toFixed(2) + "K";
      }

      return totalSum;
    },
    unrealizedPnLPercentage() {
      const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
      let totalPnL = 0;
      let totalVal = 0;
      this.positions.forEach((p) => {
        const qty = num(p.quantity);
        const avg = num(p.avgCost);
        const last = p.last != null ? num(p.last) : 95; //mocked

        totalPnL += (last - avg) * qty;
        totalVal += last * qty;
      });

      if (totalVal === 0) return 0;
      return (totalPnL / totalVal) * 100;
    },
  },
});
